package org.gstechnovos.jexonplastech.sections.home.sections

import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.ObjectFit
import com.varabyte.kobweb.compose.css.TextDecorationLine
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.layout.SimpleGrid
import com.varabyte.kobweb.silk.components.layout.numColumns
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import org.gstechnovos.jexonplastech.sections.home.styles.CategoryCardStyle
import org.gstechnovos.jexonplastech.sections.home.styles.CategoryTitleStyle
import org.gstechnovos.jexonplastech.sections.home.styles.FeaturedCategoriesContainerStyle
import org.gstechnovos.jexonplastech.theme.toSitePalette
import org.gstechnovos.jexonplastech.theme.unextractable
import org.gstechnovos.jexonplastech.util.LocalBreakpoint
import org.gstechnovos.jexonplastech.util.Res
import org.gstechnovos.jexonplastech.util.animateSlideUp
import org.gstechnovos.jexonplastech.util.rememberScrollVisibility
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.FlexDirection
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Div

// ==========================================
// CMS DATA MODEL
// ==========================================

data class CategoryCardData(
    val title: String, val imagePath: String, val linkPath: String
)

/**
 * Images (Products):Dimensions/Aspect Ratio: Minimum 400x400px; strictly 1:1 (Square) aspect ratio.
 * Visual Constraint: Use images with a transparent or pure white background.
 * Rendering Behavior: Renders within a forced square container using ObjectFit.
 * Contain with 16px of padding around the image.
 *
 * Text:Headline: Renders at 36px on desktop and 28px on mobile. Limit to 3-6 words.
 * Subheadline: Renders at 16px. Limit to 1-2 sentences.
 * Category Titles: Renders at 18px. Limit to 1-3 words (e.g., "Heavy-Duty Brooms")
 * to prevent text wrapping on mobile devices.
 */
data class HomeFeaturedCategoriesData(
    val headline: String, val subheadline: String, val categories: List<CategoryCardData>
)

val defaultFeaturedCategoriesData = HomeFeaturedCategoriesData(
    headline = "Explore Our Product Lines",
    subheadline = "Browse our extensive catalog of durable, high-quality homeware products manufactured for everyday excellence.",
    categories = listOf(
        CategoryCardData(
            title = "Cleaning Brushes",
            imagePath = Res.Image.Home.Products.BRUSHES,
            linkPath = "/products?category=brushes"
        ), CategoryCardData(
            title = "Floor Wipers", imagePath = Res.Image.Home.Products.WIPERS, linkPath = "/products?category=wipers"
        ), CategoryCardData(
            title = "Heavy-Duty Brooms",
            imagePath = Res.Image.Home.Products.BROOMS,
            linkPath = "/products?category=brooms"
        ), CategoryCardData(
            title = "Vegetable Baskets",
            imagePath = Res.Image.Home.Products.BASKETS,
            linkPath = "/products?category=baskets"
        )
    )
)

// ==========================================
// COMPONENT
// ==========================================

@Composable
fun HomeFeaturedCategoriesSection(data: HomeFeaturedCategoriesData = defaultFeaturedCategoriesData) {
    val breakpoint = LocalBreakpoint.current
    val isDesktop = breakpoint >= Res.Breakpoints.LG
    val palette = ColorMode.current.toSitePalette()

    val (isVisible, visibilityRef) = rememberScrollVisibility(threshold = 0.15)

    Column(
        modifier = FeaturedCategoriesContainerStyle.toModifier().padding(
            topBottom = if (isDesktop) 80.px else 40.px, leftRight = 5.percent
        ), horizontalAlignment = Alignment.CenterHorizontally
    ) {
        Div(
            attrs = Modifier.fillMaxWidth().display(DisplayStyle.Flex).flexDirection(FlexDirection.Column)
                .alignItems(com.varabyte.kobweb.compose.css.AlignItems.Center).toAttrs {
                    ref { element ->
                        visibilityRef(element)
                        onDispose {}
                    }
                }) {
            // --- SECTION HEADER ---
            Column(
                modifier = Modifier.maxWidth(800.px).margin(bottom = 50.px).animateSlideUp(isVisible, delayMs = 0),
                horizontalAlignment = Alignment.CenterHorizontally
            ) {
                SpanText(
                    text = data.headline,
                    modifier = Modifier.color(palette.custom.darkText).fontSize(if (isDesktop) 36.px else 28.px)
                        .fontWeight(FontWeight.Bold).margin(bottom = 16.px)
                        .textAlign(com.varabyte.kobweb.compose.css.TextAlign.Center)
                )
                SpanText(
                    text = data.subheadline,
                    modifier = Modifier.color(palette.custom.darkText.toRgb().copyf(alpha = 0.7f)).fontSize(16.px)
                        .lineHeight(1.6).textAlign(com.varabyte.kobweb.compose.css.TextAlign.Center)
                )
            }

            // --- CATEGORY GRID ---
            SimpleGrid(
                // 2 columns on mobile/tablet, 4 columns on desktop/large screens
                numColumns(2, lg = 4),
                modifier = Modifier.fillMaxWidth().maxWidth(1200.px).gap(if (isDesktop) 30.px else 16.px)
            ) {
                data.categories.forEachIndexed { index, category ->
                    Box(
                        modifier = Modifier.fillMaxWidth().height(100.percent)
                            .animateSlideUp(isVisible, delayMs = 150 + (index * 150))
                    ) {
                        CategoryCard(category)
                    }
                }
            }
        }
    }
}

@Composable
private fun CategoryCard(data: CategoryCardData) {
    val palette = ColorMode.current.toSitePalette()
    var isHovered by remember { mutableStateOf(false) }

    Link(
        path = data.linkPath,
        modifier = Modifier.textDecorationLine(TextDecorationLine.None).display(DisplayStyle.Block).fillMaxWidth()
            .height(100.percent)
    ) {
        Column(
            modifier = CategoryCardStyle.toModifier().fillMaxWidth().height(100.percent)
            .onMouseEnter { isHovered = true }.onMouseLeave { isHovered = false },
            horizontalAlignment = Alignment.CenterHorizontally,
            verticalArrangement = Arrangement.Top
        ) {
            // Product Image Container
            Box(
                modifier = Modifier.fillMaxWidth().aspectRatio(1) // Forces a perfect square
                    .margin(bottom = 20.px).backgroundColor(palette.custom.white), contentAlignment = Alignment.Center
            ) {
                Image(
                    src = data.imagePath,
                    alt = data.title,
                    modifier = Modifier.fillMaxSize()
                        .objectFit(ObjectFit.Contain) // Contain ensures the whole product is visible
                        .padding(16.px).unextractable() // Asset protection
                )
            }

            // Category Title
            SpanText(
                text = data.title,
                modifier = CategoryTitleStyle.toModifier().fontSize(18.px).fontWeight(FontWeight.Bold)
                    .textAlign(com.varabyte.kobweb.compose.css.TextAlign.Center)
                    .then(if (isHovered) Modifier.color(palette.brand.primaryBlue) else Modifier)
            )
        }
    }
}